import { type CSSObject } from '@emotion/react';
import { useTheme } from '../../../../theme';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';
import { listClasses } from '@mui/material/List';
import { paperClasses } from '@mui/material/Paper';
import {
  multiSectionDigitalClockClasses,
  multiSectionDigitalClockSectionClasses,
} from '@mui/x-date-pickers';

type Return = Record<
  'digitalClock' | 'digitalClockItem' | 'icons' | 'popper' | 'input',
  CSSObject
>;
export const useTimeInputStyles = (): Return => {
  const theme = useTheme();

  return {
    popper: {
      [`.${paperClasses.root}`]: {
        color: theme.icon.secondary,
        padding: theme.spacing(4),
        borderRadius: theme.shape.radius12,
        boxShadow: theme.shadows[1],
        border: `1px solid ${theme.popover.border}`,
        marginTop: theme.spacing(1),
      },
    },
    digitalClock: {
      [`& .${multiSectionDigitalClockClasses.root}`]: {
        border: 'none',
        gap: '10px',
      },
      [`& .${listClasses.padding}.${multiSectionDigitalClockSectionClasses.root}`]:
        {
          border: 'none',
          /**
           * Make the first 7 items visible in the popper(12, 1, 2, 3, 4, 5, 6).
           */
          maxHeight: 300,
          width: '60px',
        },
    },
    digitalClockItem: {
      [`& .${multiSectionDigitalClockSectionClasses.item}`]: {
        borderRadius: theme.shape.radius8,
        margin: theme.spacing(0, 0, 2, 0),
        width: '60px',
        ...theme.typography.bodyS,
        color: theme.text.generic.secondary,
        fontWeight: theme.typography.fontWeightRegular,
        backgroundColor: theme.datepicker.cells.default,
      },
      [`& .${multiSectionDigitalClockSectionClasses.item}:hover`]: {
        backgroundColor: theme.datepicker.cells.hover,
        color: theme.text.generic.primary,
        fontWeight: theme.typography.fontWeightSemiBold,
      },
      [`& .${multiSectionDigitalClockSectionClasses.item}:focus`]: {
        backgroundColor: theme.datepicker.cells.hover,
      },
      [`& .${multiSectionDigitalClockSectionClasses.item}.Mui-selected`]: {
        backgroundColor: theme.datepicker.cells.selected,
        color: theme.text.generic.primary,
        fontWeight: theme.typography.fontWeightSemiBold,
        ':hover': {
          backgroundColor: theme.datepicker.cells.hover,
        },
      },
    },
    icons: {
      // we clear out any margins MUI adds as we set our own
      [`& .${inputAdornmentClasses.root}.${inputAdornmentClasses.positionEnd}`]:
        {
          margin: theme.spacing(0),
        },
      '& .clearButton': {
        // The MUI clear button is hidden by default, and only shown when the input is focused
        opacity: 1,
      },
    },
    input: {
      [`& .${inputBaseClasses.root}`]: {
        // There is not enough specificity to override base styles
        gap: '0 !important',

        [`& .${inputBaseClasses.input}`]: {
          padding: theme.spacing(0, 2),
        },
      },
    },
  };
};
